import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { LoadingPane, SaveButton, ImageDisplay } from '@social-solutions/ssg-forms';
import { ResponsesList } from './responses-list';
import { track } from '../tracking';
import { features, RequireFeatureComponent } from '../features';
import { integrationTypes } from '../tenantInfo';
import * as touchpointsActions from '../touchpoints/touchpoints.actions';
import * as responsesActions from './responses.actions';
import { ssgFormsAdapters } from '../common';

const pageSizeIncrement = 10;

const emptyText = "As you begin to fill out and submit forms, a history of those submissions will be accessible from this screen.";
const emptyTextFilter = "There are no Form Submissions for the current search criteria.";

/** Displays all form responses for a given formId (an empty guid can be provided to search for all form responses) */
export class ViewResponsesComponent extends Component {

  /**
   * Redirects to the correct view page, when the view button is clicked.
   * @param {*} item
   */
  viewResponse( item ) {
    const { touchpointsActions, redirectUrl } = this.props;
    var redirectString = redirectUrl ? '?redirectUrl=' + redirectUrl : '';
    const programInformation = item.programs && item.programs.length > 0 ? item.programs[0] : {};
    touchpointsActions.redirectTo('sites/' + programInformation.siteId + '/programs/' + programInformation.programId + '/forms/' + item.formId + '/responses/' + item.id + '/view' + redirectString);
  }

  /**
   * Redirects to the correct edit page, when the edit button is clicked.
   * @param {*} item
   */
  editResponse( item ) {
    const { touchpointsActions, redirectUrl } = this.props;
    var redirectString = redirectUrl ? '?redirectUrl=' + redirectUrl : '';
    const programInformation = item.programs && item.programs.length > 0 ? item.programs[0] : {};
    touchpointsActions.redirectTo('sites/' + programInformation.siteId + '/programs/' + programInformation.programId + '/forms/' + item.formId + '/responses/' + item.id + '/edit' + redirectString);
  }

  /** handles loading the form responses */
  loadResponses(props) {
    const { touchpointsActions, responses, formId } = props;
    const { pagingData, nameSearch, siteId, programId } = responses;
    const { currentPage, itemsPerPage, sortBy, sortAsc } = pagingData || {};

    touchpointsActions.fetchTouchpointResponses( formId,
        1, //maxRecords - fetchAll takes priority over this
        currentPage,  //page - to get the most recent ones, take the first page
        itemsPerPage, // per page
        true, // fetch all, so that we know how many total responses there are.
        sortBy,  // default sort is by date...
        sortAsc, // in descending order.
        nameSearch,
        siteId,
        programId,
      );
  }

  componentDidMount() {
    this.loadResponses(this.props);
  }

  componentWillUpdate(nextProps, nextState) {
    const { touchpoints, formId } = nextProps || {};
    const { pagedFormResponses } = touchpoints || {};
    const responsesState = pagedFormResponses && pagedFormResponses.get(formId);
    const { isFetching, isValid } = responsesState || {};
    if(!responsesState || !(isFetching || isValid))
      this.loadResponses(nextProps);
  }

  /**
   * changes the number of items shown on the page,
   * @param {*} numItems the new number of items to show on the page
   */
  handleChangePerPage(numItems) {
    const { changePerPageNumber } = this.props.responsesActions;
    track.event.dashboard.recentResponses.itemsPerPage(numItems);
    changePerPageNumber(numItems);
  }

  render() {
    const { touchpoints, responses, formId, programEnrollment } = this.props;
    const touchpointResponseState = touchpoints.pagedFormResponses;
    const responsesState = touchpointResponseState && touchpointResponseState.get(formId);
    const { isFetching : isLoading, data : responsesData } = responsesState || {};

    const { pagingData, nameSearch, programId, siteId } = responses;
    const { itemsPerPage } = pagingData || {};
    const { results, totalPages } = responsesData || {};

    var sectionProps = [];
    results && results.forEach(response => {
      var date = moment.utc(response.responseCreatedDate);
      var title = date.isSame(moment(), 'day') ? "Today" : date.format("MMMM DD, YYYY");
      var existingSection = sectionProps.find(val => val.sectionTitle === title);
      if(existingSection) {
        existingSection.responses.push(response);
      }
      else {
        sectionProps.push({
          id: title,
          sectionTitle: title,
          responses: [response]
        });
      }
    });

    const hasFilter = !!nameSearch || !!programId || !!siteId;

    return (
      <RequireFeatureComponent
        requiredFeatures={[features.apricotPortalTier2]}
        requiredForIntegrationTypes={[integrationTypes.apricot]}>
        <LoadingPane
          loadingState={ssgFormsAdapters.convertLoadingState(responsesState)}
          loadingMessage="Loading Form Submissions">
          {results && results.length > 0 ? (
            <div>
              {sectionProps && sectionProps.map((val, index) => <ResponsesList key={index} {...val} programEnrollment={programEnrollment} viewResponse={(item) => this.viewResponse(item)} editResponse={(item) => this.editResponse(item)} search={nameSearch} />)}
              {totalPages && totalPages > 1 ? (
                <div className="responses-actions">
                  <SaveButton
                    className="ssg-forms-btn ssg-forms-btn-secondary"
                    display="Show More"
                    savingDisplay="Loading"
                    saving={isLoading}
                    onClick={() => this.handleChangePerPage(itemsPerPage + pageSizeIncrement)}
                    css={{
                      button: "ssg-forms-btn ssg-forms-btn-secondary",
                      buttonSaving: "ssg-forms-btn ssg-forms-btn-secondary disabled"
                    }} />
                </div>
              ) : null}
            </div>
          ) : (
            <div id="empty-display" className="centered-content">
              <ImageDisplay
                src="/Submission.png"
                primaryText={"No Form Submissions" + (hasFilter ? "" : " Yet")}
                secondaryText={hasFilter ? emptyTextFilter : emptyText}/>
            </div>
          )}
        </LoadingPane>
      </RequireFeatureComponent>
    );
  }
}

ViewResponsesComponent.defaultProps = {
  redirectUrl : 'responses/list'
}

ViewResponsesComponent.propTypes = {
  formId: PropTypes.string.isRequired,
  touchpointsActions: PropTypes.shape({
    fetchTouchpointResponses: PropTypes.func.isRequired
  }).isRequired,
  touchpoints: PropTypes.object.isRequired,
  responsesActions: PropTypes.shape({
    changePageNumber: PropTypes.func.isRequired,
    changePerPageNumber: PropTypes.func.isRequired,
    changeSort: PropTypes.func.isRequired
  }),
  responses: PropTypes.object.isRequired,
  redirectUrl: PropTypes.string,
};

const mapStateToProps = ({touchpoints, responses, programEnrollment}) => {
  return {
    touchpoints,
    responses,
    programEnrollment,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    touchpointsActions: bindActionCreators(touchpointsActions, dispatch),
    responsesActions: bindActionCreators(responsesActions, dispatch)
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ViewResponsesComponent);



// WEBPACK FOOTER //
// src/responses/view-responses.component.js